import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Layout from "../components/Layout";
import { GlobalStyle } from "../pages/HomePage";
import { graphql, Link } from "gatsby";
import Adsense from "react-adsense";
import ReactGA from "react-ga";
import ShareIcons from "../components/ShareIcons";
import AdCardInDetailPage from "../components/AdCardInDetailPage";
import SEO from "../components/SEO";
import { optimizeImageUrl } from "../lib/prismicImage";
ReactGA.initialize("UA-141669883-1");

/* ─── Styles ─── */

const ArticleHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 1rem;
  @media (min-width: 640px) { padding: 2rem 1.5rem 1rem; }
  @media (min-width: 1024px) { padding: 2rem 2rem 1rem; }
`;

const ArticleTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.3;
  margin: 0 0 0.75rem;
  @media (min-width: 768px) { font-size: 2rem; }
`;

const PhotoCount = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
  margin: 0 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const AdWrap = styled.div`
  width: 100%;
  margin-bottom: 1rem;
`;

/* ─── Carousel ─── */

const GallerySection = styled.section`
  background: var(--bg-secondary);
  padding: 1.5rem 0 2rem;
`;

const GalleryInner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const MainImageWrap = styled.div`
  position: relative;
  background: #000;
  border-radius: 0.75rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  max-height: 520px;
`;

const MainImage = styled.img`
  max-width: 100%;
  max-height: 520px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.dir === 'prev' ? 'left: 1rem;' : 'right: 1rem;'}
  background: rgba(0,0,0,0.55);
  color: white;
  border: none;
  border-radius: 9999px;
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 2;
  &:hover { background: rgba(0,0,0,0.8); }
`;

const Counter = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(0,0,0,0.65);
  color: white;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  z-index: 2;
`;

const ThumbnailStrip = styled.div`
  margin-top: 1.25rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: 0.6rem;
  justify-content: center;
  min-width: max-content;
  padding: 0 0.25rem;
`;

const Thumbnail = styled.button`
  flex-shrink: 0;
  border-radius: 0.4rem;
  overflow: hidden;
  border: 3px solid ${props => props.active ? 'var(--link)' : 'var(--border)'};
  opacity: ${props => props.active ? 1 : 0.65};
  cursor: pointer;
  padding: 0;
  background: none;
  transition: border-color 0.15s, opacity 0.15s;
  &:hover { opacity: 1; border-color: var(--link); }
  img {
    width: 72px;
    height: 72px;
    object-fit: cover;
    display: block;
  }
`;

const NavHint = styled.p`
  text-align: center;
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ContentSection = styled.section`
  background: var(--bg-secondary);
  padding: 2rem 0;
`;

const ContentInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) {
    padding: 0 2rem;
    grid-template-columns: 2fr 1fr;
  }
`;

const Sidebar = styled.div`
  display: flex;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
`;

const ShareRow = styled.div`
  margin-bottom: 1.25rem;
`;

/* ─── Related ─── */

const RelatedSection = styled.section`
  background: var(--bg);
  border-top: 1px solid var(--border);
  padding: 2rem 0;
`;

const RelatedInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const RelatedTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1.5rem;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 640px) { grid-template-columns: 1fr 1fr; }
  @media (min-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
`;

const RelatedCardImg = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  overflow: hidden;
  background: var(--bg-tertiary);
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
`;

const RelatedCardTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--compact-title);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.45;
  transition: color 0.2s;
`;

const RelatedCardContent = styled.div`
  padding: 0.75rem;
`;

const RelatedCardExternal = styled.a`
  display: block;
  text-decoration: none;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.15); transform: translateY(-2px); }
  &:hover img { transform: scale(1.05); }
  &:hover h3 { color: var(--link); }
`;

const RelatedCardInternal = styled(Link)`
  display: block;
  text-decoration: none;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.15); transform: translateY(-2px); }
  &:hover img { transform: scale(1.05); }
  &:hover h3 { color: var(--link); }
`;

/* ─── Carousel component ─── */

const GalleryCarousel = ({ images }) => {
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'ArrowLeft')  setCurrent(i => i === 0 ? images.length - 1 : i - 1);
            if (e.key === 'ArrowRight') setCurrent(i => i === images.length - 1 ? 0 : i + 1);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [images.length]);

    if (!images || images.length === 0) return null;

    const prev = () => setCurrent(i => i === 0 ? images.length - 1 : i - 1);
    const next = () => setCurrent(i => i === images.length - 1 ? 0 : i + 1);
    const img = images[current];

    return (
        <>
            <MainImageWrap>
                {img && img.img && img.img.url && (
                    <MainImage src={optimizeImageUrl(img.img.url, 1200)} alt={`Gallery image ${current + 1}`} />
                )}
                <NavBtn dir="prev" onClick={prev} aria-label="Previous">
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </NavBtn>
                <NavBtn dir="next" onClick={next} aria-label="Next">
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </NavBtn>
                <Counter>{current + 1} / {images.length}</Counter>
            </MainImageWrap>

            <ThumbnailStrip>
                <ThumbnailRow>
                    {images.map((item, i) => (
                        <Thumbnail key={i} active={i === current} onClick={() => setCurrent(i)} aria-label={`Image ${i + 1}`}>
                            {item && item.img && item.img.url && (
                                <img src={optimizeImageUrl(item.img.url, 200)} alt={`Thumbnail ${i + 1}`} />
                            )}
                        </Thumbnail>
                    ))}
                </ThumbnailRow>
            </ThumbnailStrip>

            <NavHint>Use arrow keys or buttons to navigate</NavHint>
        </>
    );
};

/* ─── Page component ─── */

class Article extends React.Component {
    componentDidMount() {
        const ele = document.getElementById("ipl-progress-indicator");
        if (ele) ele.outerHTML = "";
    }

    render() {
        const prismicGalleryarticle = this.props.data.prismicGalleryarticle;
        const uidPath = prismicGalleryarticle.uid;
        ReactGA.pageview(uidPath);
        ReactGA.event({ category: "Gallery", action: "Card Click" });

        const article = prismicGalleryarticle.data;
        const images = article.group || [];

        const title = article.title && article.title.text;
        const description = title
            ? `${title} — browse photos on Andhra Headlines.`
            : "Photo gallery on Andhra Headlines.";
        const coverImg =
            (article.img && article.img.url) ||
            (images[0] && images[0].img && images[0].img.url) ||
            undefined;
        const datePublished = prismicGalleryarticle.first_publication_date;
        const dateModified = prismicGalleryarticle.last_publication_date;
        const tags = (prismicGalleryarticle.tags || []).filter(t => t && t !== "featured");
        const relatedArticles = (this.props.data.relatedArticles && this.props.data.relatedArticles.edges) || [];

        return (
            <>
                <SEO
                    title={title}
                    description={description}
                    image={coverImg}
                    pathname={`/${uidPath}`}
                    article
                    datePublished={datePublished}
                    dateModified={dateModified}
                    keywords={tags}
                />
                <GlobalStyle />
                <Layout>
                    {/* ── Header ── */}
                    <ArticleHeader>
                        <AdWrap className="ad-container">
                            <Adsense.Google
                                client="ca-pub-5630104718741028"
                                slot="9699731559"
                                style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                                format="auto"
                                responsive="true"
                            />
                        </AdWrap>
                        <ArticleTitle>{article.title && article.title.text}</ArticleTitle>
                        {images.length > 0 && (
                            <PhotoCount>
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {images.length} {images.length === 1 ? 'Photo' : 'Photos'}
                            </PhotoCount>
                        )}
                    </ArticleHeader>

                    {/* ── Carousel ── */}
                    <GallerySection>
                        <GalleryInner>
                            {images.length > 0 ? (
                                <GalleryCarousel images={images} />
                            ) : (
                                <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '3rem 0' }}>
                                    No images in this gallery.
                                </p>
                            )}
                        </GalleryInner>
                    </GallerySection>

                    <ContentSection>
                        <ContentInner>
                            <div>
                                <ShareRow>
                                    <ShareIcons path={uidPath} />
                                </ShareRow>
                            </div>
                            <Sidebar>
                                <AdCardInDetailPage
                                    client="ca-pub-5630104718741028"
                                    slot="9368762673"
                                    style={{ display: 'block', height: 280, maxWidth: 336, width: '100%', marginLeft: 'auto', marginRight: 'auto' }}
                                    layout="-6t+ed+2i-1n-4w"
                                    format="fluid"
                                />
                            </Sidebar>
                        </ContentInner>
                    </ContentSection>

                    {/* ── Related Articles ── */}
                    {relatedArticles.filter(e => e.node.uid !== uidPath).length > 0 && (
                        <RelatedSection>
                            <RelatedInner>
                                <RelatedTitle>Related News</RelatedTitle>
                                <RelatedGrid>
                                    {relatedArticles
                                        .filter(e => e.node.uid !== uidPath)
                                        .slice(0, 9)
                                        .map((e, i) => {
                                            const a = e.node;
                                            const isExternal = !!(a.data && a.data.third_party_url);
                                            const title = a.data && a.data.title && a.data.title.text ? a.data.title.text : '';
                                            const imgUrl = a.data && a.data.img && a.data.img.url ? a.data.img.url : null;
                                            const CardEl = isExternal ? RelatedCardExternal : RelatedCardInternal;
                                            const linkProps = isExternal
                                                ? { href: a.data.third_party_url.startsWith('http') ? a.data.third_party_url : `https://${a.data.third_party_url}`, target: '_blank', rel: 'noopener noreferrer' }
                                                : { to: `/${a.uid}/` };
                                            return (
                                                <CardEl key={i} {...linkProps}>
                                                    <RelatedCardImg>
                                                        {imgUrl && <img src={optimizeImageUrl(imgUrl, 320)} alt={title} />}
                                                    </RelatedCardImg>
                                                    <RelatedCardContent>
                                                        <RelatedCardTitle>{title}</RelatedCardTitle>
                                                    </RelatedCardContent>
                                                </CardEl>
                                            );
                                        })
                                    }
                                </RelatedGrid>
                            </RelatedInner>
                        </RelatedSection>
                    )}
                </Layout>
            </>
        );
    }
}

export default Article;

export const pageQuery = graphql`
    query GalleryArticleBySlug($uid: String!, $tags: [String]!) {
        prismicGalleryarticle(uid: { eq: $uid }) {
            uid
            tags
            first_publication_date
            last_publication_date
            data {
                img { url }
                group { img { url } }
                title { text }
            }
        }
        relatedArticles: allPrismicArticle(
            filter: {
                tags: { in: $tags }
                uid: { ne: $uid }
                data: { active: { eq: "Y" } }
            }
            sort: { fields: [first_publication_date], order: DESC }
            limit: 12
        ) {
            edges {
                node {
                    uid
                    data {
                        third_party_url
                        title { text }
                        img { url }
                        datetime
                    }
                }
            }
        }
    }
`;
